import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaBook, FaGraduationCap, FaMapMarkerAlt, FaMicroscope } from "react-icons/fa";
import "./StateSyllabus.css";
import { ENQUIRY_URL } from "../config";

const ICSE = () => {
  const highlights = [
    {
      icon: "🏛️",
      title: "Available Since",
      value: "1999",
      description: "Sri Vani has offered ICSE curriculum since 1999",
    },
    {
      icon: "🎓",
      title: "Affiliated To",
      value: "CISCE",
      description: "Council for the Indian School Certificate Examinations",
    },
    {
      icon: "📚",
      title: "Standards",
      value: "Pre-Nursery – 10th",
      description: "From Pre-Nursery through 10th Standard",
    },
    {
      icon: "🏫",
      title: "Campuses",
      value: "2 Campuses",
      description: "Rajajinagar & Basaveshwaranagar",
    },
  ];

  const curriculumLevels = [
    {
      level: "Primary — 1st & 2nd",
      icon: <FaBook />,
      subjects: [
        "English",
        "Mathematics",
        "Environmental Science",
        "Kannada / Hindi / Sanskrit",
        "Computers",
        "Physical Education",
        "General Knowledge",
        "Moral Science",
        "Music & Dance",
      ],
    },
    {
      level: "Elementary — 3rd & 4th",
      icon: <FaBook />,
      subjects: [
        "English",
        "Mathematics & Science",
        "Social Studies",
        "Kannada / Hindi / Sanskrit",
        "Computers",
        "Physical Education",
        "General Knowledge",
        "Moral Science",
        "Music & Dance",
      ],
    },
    {
      level: "Middle — 5th to 7th",
      icon: <FaGraduationCap />,
      subjects: [
        "English Literature & Language",
        "Mathematics & Science",
        "Social Studies",
        "Computers",
        "Physical Education",
        "2nd Language (Kannada / Sanskrit / Hindi / French)",
        "3rd Language (Kannada / Hindi)",
      ],
    },
    {
      level: "Secondary — 8th to 10th",
      icon: <FaMicroscope />,
      subjects: [
        "English Literature & Language",
        "Mathematics",
        "Physics, Chemistry & Biology",
        "History & Geography",
        "Languages (Kannada / Sanskrit / Hindi / French)",
        "Computer Applications",
        "Physical Education",
        "Indian Dance & Yoga (electives)",
      ],
    },
  ];

  const campuses = [
    {
      name: "Rajajinagar Campus",
      subtitle: "Ramavana",
      tagline: "A Burgeoning Campus that Vibrates Sentience.",
      image: "/images/state-syllabus/rajaji-nagar-campus.jpg",
      link: "/campuses/rajajinagar",
    },
    {
      name: "Basaveshwaranagar Campus",
      subtitle: "Shamavana",
      tagline: "An Ambidextrous hub of Academic Excellence, Culture & Art.",
      image: "/images/state-syllabus/basaveshwara-nagar-campus.jpg",
      link: "/campuses/basaveshwaranagar",
    },
  ];

  return (
    <div className="state-syllabus-page">
      {/* Page Header */}
      <section
        className="ss-page-header"
        style={{ backgroundImage: "url('/images/icse/icse-hero.jpg')" }}
      >
        <div className="ss-header-overlay" />
        <div className="container">
          <motion.div
            className="ss-header-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="ss-header-tag">Council for the Indian School Certificate Examinations</p>
            <h1>ICSE</h1>
            <p className="ss-header-sub">Excel in Language, Arts &amp; Science</p>
          </motion.div>
        </div>
      </section>

      {/* Quick Highlights */}
      <section className="ss-highlights-section">
        <div className="container">
          <div className="ss-highlights-grid">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="ss-highlight-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="ss-highlight-icon">{item.icon}</div>
                <div className="ss-highlight-value">{item.value}</div>
                <div className="ss-highlight-title">{item.title}</div>
                <div className="ss-highlight-desc">{item.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emblem + Bullets Section */}
      <section className="ss-emblem-section">
        <div className="container">
          <div className="ss-emblem-row">
            <motion.div
              className="ss-emblem-col"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/images/icse/cisce-badge.jpg"
                alt="CISCE Badge"
                className="ss-emblem-img"
              />
            </motion.div>
            <motion.div
              className="ss-bullets-col"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ul className="ss-key-bullets">
                <li>Available at Sri Vani Since 1999</li>
                <li>Part of CISCE Umbrella</li>
                <li>Excel in Language, Arts &amp; Science</li>
                <li>Available at Rajajinagar Campus</li>
                <li>Also at Basaveshwaranagar Campus</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="ss-description-section">
        <div className="container">
          <motion.div
            className="ss-description-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p>
              ICSE is considered more challenging compared to other boards due to
              the detailed study of each subject. The board places emphasis on
              practical knowledge, analytical skills, diligence, and time management,
              giving students strong subject fundamentals that benefit all higher
              education pursuits.
            </p>
            <p>
              The syllabus offers a rich learning environment not restricted to rote
              learning. Project work, research, group assignments, and internal
              assessments form an integral part of the curriculum. The board aligns
              with foreign university requirements and builds verbal ability for
              competitive exams like GRE, TOEFL, and GMAT.
            </p>
            <div className="ss-desc-cta">
              <a
                href={ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Curriculum Section */}
      <section className="ss-subjects-section section">
        <div className="container">
          <motion.div
            className="ss-subjects-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="ss-section-title">ICSE Curriculum Pathway</h2>
            <p className="ss-section-subtitle">
              Structured learning from Pre-Nursery through 10th Standard
            </p>
          </motion.div>

          <div className="cbse-curriculum-grid icse-curriculum-grid">
            {curriculumLevels.map((level, index) => (
              <motion.div
                key={index}
                className="ss-subject-block"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="ss-subject-block-header">
                  {level.icon}
                  <h3>{level.level}</h3>
                </div>
                <ul className="ss-subject-list">
                  {level.subjects.map((sub, idx) => (
                    <li key={idx}>{sub}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="ss-subject-note icse-language-note"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p>
              <strong>Language Rule:</strong> If the optional 2nd Language is Kannada,
              the 3rd Language defaults to Hindi. If the optional 2nd Language is
              Sanskrit, Hindi, or French, the 3rd Language defaults to Kannada.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Campuses Section */}
      <section className="ss-campuses-section section">
        <div className="container">
          <motion.div
            className="ss-campuses-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="ss-section-tag">
              <FaMapMarkerAlt /> Pursue at following Campuses
            </div>
            <h2 className="ss-section-title">Our ICSE Campuses</h2>
          </motion.div>

          <div className="ss-campuses-grid">
            {campuses.map((campus, index) => (
              <motion.div
                key={index}
                className="ss-campus-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div className="ss-campus-image">
                  <img src={campus.image} alt={campus.name} />
                  <div className="ss-campus-overlay">
                    <span>{campus.subtitle}</span>
                  </div>
                </div>
                <div className="ss-campus-info">
                  <h3>{campus.name}</h3>
                  <p>{campus.tagline}</p>
                  <Link to={campus.link} className="btn btn-outline ss-explore-btn">
                    Explore Campus
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ss-cta-section">
        <div className="container">
          <motion.div
            className="ss-cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Begin Your ICSE Journey at Sri Vani</h2>
            <p>
              Join a tradition of rigorous, holistic education that builds
              language, analytical, and scientific excellence since 1999.
            </p>
            <div className="ss-cta-buttons">
              <a
                href={ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply for Admission
              </a>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ICSE;
